import { Button } from '@/components/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/dialog'
import { Notification, NotificationState } from '@frontend/core'
import { readNotification } from '@frontend/core/lib/modules/notifications'
import { useAppDispatch, useAppSelector } from '@frontend/react'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Download,
  Info
} from 'lucide-react'
import { useEffect, useState } from 'react'

// Icon mapping for notification types
const iconMap = {
  general: <Info className='h-10 w-10 text-blue-500' />,
  error: <AlertCircle className='h-10 w-10 text-red-500' />,
  success: <CheckCircle className='h-10 w-10 text-green-500' />,
  info: <Info className='h-10 w-10 text-blue-500' />,
  download: <Download className='h-10 w-10 text-purple-500' />,
  warning: <AlertTriangle className='h-10 w-10 text-yellow-500' />
}

interface RootState {
  notifications: NotificationState
}

export function ModalNotification() {
  const dispatch = useAppDispatch()
  const notifications = useAppSelector(
    (state: RootState) => state.notifications
  )

  // State for managing modal queue
  const [currentModal, setCurrentModal] = useState<Notification | null>(null)
  const [modalQueue, setModalQueue] = useState<Notification[]>([])
  const [isOpen, setIsOpen] = useState(false)

  // Get unread modal notifications
  const getUnreadModalNotifications = () => {
    return Object.values(notifications.entities)
      .filter((n) => !n.read && n.displayType === 'modal')
      .sort((a, b) => a.timestamp - b.timestamp) // FIFO order
  }

  // Process modal queue
  useEffect(() => {
    const unreadModals = getUnreadModalNotifications()

    if (!currentModal && unreadModals.length > 0) {
      // Show the first unread modal
      const nextModal = unreadModals[0]
      setCurrentModal(nextModal)
      setModalQueue(unreadModals.slice(1))
      setIsOpen(true)
    } else if (currentModal && unreadModals.length > 1) {
      // Update queue if new modals arrived while one is open
      const currentModalStillExists = unreadModals.find(
        (n) => n.id === currentModal.id
      )
      if (currentModalStillExists) {
        setModalQueue(unreadModals.filter((n) => n.id !== currentModal.id))
      }
    }
  }, [notifications.entities, currentModal])

  // Handle modal dismissal
  const handleDismiss = (notificationId: string) => {
    // Mark notification as read
    dispatch(readNotification(notificationId))

    // Close current modal
    setIsOpen(false)
    setCurrentModal(null)

    // Show next modal in queue after a brief delay
    setTimeout(() => {
      if (modalQueue.length > 0) {
        const nextModal = modalQueue[0]
        setCurrentModal(nextModal)
        setModalQueue((prev) => prev.slice(1))
        setIsOpen(true)
      }
    }, 100)
  }

  // Handle primary action
  const handlePrimaryAction = () => {
    if (currentModal?.action?.onClick) {
      currentModal.action.onClick(currentModal.data ?? {})
    }
    handleDismiss(currentModal!.id)
  }

  // Handle secondary action
  const handleSecondaryAction = () => {
    if (currentModal?.secondaryAction?.onClick) {
      currentModal.secondaryAction.onClick()
    }
    handleDismiss(currentModal!.id)
  }

  // Handle cancel (for warnings or general dismissal)
  const handleCancel = () => {
    handleDismiss(currentModal!.id)
  }

  if (!currentModal) {
    return null
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleCancel()
        }
      }}
    >
      <DialogContent
        className='text-center sm:max-w-md'
        role='dialog'
        aria-labelledby='modal-title'
        aria-describedby='modal-description'
      >
        <DialogHeader className='pb-6 text-center'>
          <div className='mb-6 flex justify-center'>
            {iconMap[currentModal.type]}
          </div>
          {currentModal.title && (
            <DialogTitle
              id='modal-title'
              className='text-center text-xl font-semibold'
            >
              {currentModal.title}
            </DialogTitle>
          )}
        </DialogHeader>

        <div className='px-2 py-4'>
          <div
            id='modal-description'
            className='text-muted-foreground text-center text-sm leading-relaxed whitespace-pre-line'
          >
            {currentModal.message}
          </div>
        </div>

        <DialogFooter className='flex flex-col items-center justify-center gap-3 pt-4 sm:flex-row'>
          {/* Primary Action */}
          {currentModal.action && (
            <Button
              onClick={handlePrimaryAction}
              className='w-full min-w-[100px] sm:w-auto'
            >
              {currentModal.action.label}
            </Button>
          )}

          {/* Secondary Action */}
          {currentModal.secondaryAction && (
            <Button
              variant='outline'
              onClick={handleSecondaryAction}
              className='w-full min-w-[100px] bg-transparent sm:w-auto'
            >
              {currentModal.secondaryAction.label}
            </Button>
          )}

          {/* Default Cancel for warnings or when no actions are provided */}
          {(currentModal.type === 'warning' ||
            (!currentModal.action && !currentModal.secondaryAction)) && (
            <Button
              variant='outline'
              onClick={handleCancel}
              className='w-full min-w-[100px] bg-transparent sm:w-auto'
            >
              {currentModal.type === 'warning' ? 'Cancel' : 'Close'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
